import React, { useMemo, useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTeacherSlots } from '@/hooks/use-public-api';
import { Calendar } from '@/components/ui/calendar';
import { Loader2, CalendarX2, Globe } from 'lucide-react';

interface AvailabilityCalendarProps {
  teacher: string;
  duration?: number;
  days?: number;
  selectedSlot?: string | null;
  onSelectSlot?: (iso: string) => void;
}

// Local date key (yyyy-mm-dd in the viewer's timezone) for grouping slots.
const dateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Teacher availability as a date picker + slot chips, all rendered in the
// viewer's local timezone. Slots come from the backend already filtered
// against existing bookings/sessions and the teacher's schedule.
const AvailabilityCalendar = ({
  teacher,
  duration,
  days = 14,
  selectedSlot,
  onSelectSlot,
}: AvailabilityCalendarProps) => {
  const { isRTL, language } = useLanguage();
  const { data, isLoading, isError } = useTeacherSlots(teacher, { days, duration });

  const slotsByDay = useMemo(() => {
    const map = new Map<string, { iso: string; date: Date }[]>();
    for (const iso of data?.slots ?? []) {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) continue;
      const key = dateKey(date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({ iso, date });
    }
    return map;
  }, [data?.slots]);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  // Preselect the first day that has slots (or the selected slot's day).
  useEffect(() => {
    if (selectedSlot) {
      const d = new Date(selectedSlot);
      if (!Number.isNaN(d.getTime())) {
        setSelectedDate(d);
        return;
      }
    }
    const firstKey = [...slotsByDay.keys()].sort()[0];
    if (firstKey && !selectedDate) {
      const first = slotsByDay.get(firstKey)![0].date;
      setSelectedDate(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsByDay]);

  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en', { hour: 'numeric', minute: '2-digit' }),
    [language]
  );

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="py-10 text-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" aria-hidden />
        <span className="sr-only">{isRTL ? 'جارٍ التحميل' : 'Loading'}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {isRTL ? 'تعذر تحميل مواعيد المعلم' : 'Failed to load availability'}
      </p>
    );
  }

  if (slotsByDay.size === 0) {
    return (
      <div className="py-8 text-center">
        <CalendarX2 className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" aria-hidden />
        <p className="text-sm text-muted-foreground">
          {isRTL
            ? 'لا توجد مواعيد متاحة في الأيام القادمة'
            : 'No available times in the coming days'}
        </p>
      </div>
    );
  }

  const daySlots = selectedDate ? slotsByDay.get(dateKey(selectedDate)) ?? [] : [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const rangeEnd = new Date(today);
  rangeEnd.setDate(rangeEnd.getDate() + days);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          disabled={(date) =>
            date < today || date >= rangeEnd || !slotsByDay.has(dateKey(date))
          }
          modifiers={{ hasSlots: (date) => slotsByDay.has(dateKey(date)) }}
          modifiersClassNames={{ hasSlots: 'font-semibold text-primary' }}
          className="rounded-xl border border-border"
        />
      </div>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5" aria-hidden />
          {isRTL ? 'الأوقات معروضة بتوقيتك المحلي' : 'Times are shown in your local timezone'}
        </p>
        {daySlots.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            {isRTL ? 'اختر يومًا متاحًا لعرض المواعيد' : 'Pick an available day to see times'}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2 content-start" role="listbox" aria-label={isRTL ? 'المواعيد المتاحة' : 'Available times'}>
            {daySlots.map(({ iso, date }) => {
              const isSelected = selectedSlot === iso;
              const label = timeFormatter.format(date);
              return onSelectSlot ? (
                <button
                  key={iso}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onSelectSlot(iso)}
                  className={`px-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                      : 'bg-muted/50 text-foreground hover:bg-muted border border-border'
                  }`}
                >
                  {label}
                </button>
              ) : (
                <span
                  key={iso}
                  className="px-2 py-2.5 rounded-lg text-sm font-medium text-center bg-muted/50 text-foreground border border-border"
                >
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
